import axios from "axios";
import axiosRetry from "axios-retry";
import { Toast } from "react-vant";
import { isTokenExpired, refreshToken, navigateToSignIn } from "./tokenTools";
import { isNonEmptyArray, isNonEmptyObject } from "./objectTools";
import { showDeviceModal } from "@/views/mine/device-manage/components/device-modal";
import DSBridge from "dsbridge";
import { showCustomToast } from "@/views/components/CustomToast";

const MAX_REFRESH_ATTEMPTS = 1;
let refreshAttempts = 0;
const globalCacheData = {};
const ajax = axios.create({
  baseURL: process.env.REACT_APP_API_URL_PICTURE,
  timeout: 30000,
  withCredentials: false,
});
// 避免同时发送多个刷新token的请求
let isRefreshing = false;
// 在这里添加 axios-retry 插件，指定重试策略
axiosRetry(ajax, {
  retries: 3, // 重试次数
  retryDelay: (retryCount) => {
    return retryCount * 1000; // 重试延迟，可以根据需要自定义
  },
  shouldResetTimeout: true, // 重试时是否重置超时时间
});

// 在请求拦截器中设置JWT令牌
ajax.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("access_token");
  const useToken = config.url.includes("register") ? false : true;
  if (token && useToken) {
    if (isTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
          isRefreshing = false;
        } catch (e) {
          if (e === "无刷新Token") {
            refreshAttempts = 1;
            DSBridge.call("auth.logout");
            localStorage.clear();
            navigateToSignIn("#/login/logon");
          }
        }
      }
    } else {
      const token = localStorage.getItem("access_token");
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

ajax.interceptors.response.use(
  async (response) => {
    //原生数据或者其他问题
    if (!response?.data?.code) {
      return response?.data || response;
    }
    // 多个登录错误
    if (response.data.code == 422) {
      showDeviceModal({
        message: response.data.message,
        onConfirm: () => {
          DSBridge.call("auth.logout");
          localStorage.clear();
          navigateToSignIn("#/login/logon");
        },
        onClose: () => {
          localStorage.clear();
          navigateToSignIn("#/picture/home");
        },
        isShowConfirm: true,
        confirmText: "重新登录",
      });
    }
    //登录页弹窗
    if (response.data.code == 423) {
      showCustomToast(response.data?.message);
    }
    //鉴权错误
    if (response.data.code == 402) {
      if (!localStorage.getItem("refresh_token")) {
        refreshAttempts = 0;
        Toast.fail(`${response.data.message} 请重新登录`);
        DSBridge.call("auth.logout");
        localStorage.clear();
        navigateToSignIn("#/login/logon");
      } else if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
        refreshAttempts++;
        try {
          const newToken = await refreshToken();
          if (newToken) {
            const originalRequest = response.config;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return ajax(originalRequest);
          }
        } catch {
          console.log("刷新token失败");
        }
      } else {
        refreshAttempts = 0;
        Toast.fail(`${response.data.message} 请重新登录`);
        DSBridge.call("auth.logout");
        localStorage.clear();
        navigateToSignIn("#/login/logon");
      }
    }
    //弹错误消息
    if (
      response.data.code != 200 &&
      response.data.code != 402 &&
      response.data.code != 422 &&
      response.data.code != 423
    ) {
      try {
        JSON.parse(response.data.message);
        const mes = JSON.parse(response.data.message.replace(/'/g, '"'));
        const values = Object.values(mes);
        const firstValue = values[0];
        if (response.config.toast !== "hideToast") {
          Toast.fail({
            message: firstValue,
            duration: 3000,
          });
        }
      } catch {
        if (response.config.toast !== "hideToast") {
          Toast.fail({
            message: response.data.message,
            duration: 3000,
          });
        }
      }
    }
    //缓存有效期为 5 分钟
    if (isNonEmptyObject(response.data) || isNonEmptyArray(response.data)) {
      globalCacheData[response.config.url] = {
        data: response.data,
        time: new Date().getTime() + 300000,
      };
    }
    return response.data;
  },
  (error) => {
    //能走这里 一般都是 本地问题
    Toast.fail("当前网络不稳定!");
    return Promise.reject("当前网络不稳定!");
  }
);
// 修正缓存数据处理函数
const cacheData = (args, method = "get") => {
  let url = args[0];
  let nocache = false;
  let cacheKey = url;
  let config = {};

  if (typeof url === "object" && "url" in url) {
    url = url.url;
    cacheKey = url;
  }

  // 解析参数以找到config对象和缓存控制
  if (method === "get" || method === "delete") {
    // GET/DELETE: (url, config) 或 (url, 'nocache', config)
    if (args.length >= 2) {
      if (typeof args[1] === "string" && args[1] === "nocache") {
        nocache = true;
        if (args[2] && typeof args[2] === "object") {
          config = args[2];
        }
      } else if (typeof args[1] === "object") {
        config = args[1];
        if (config.cache === "nocache") {
          nocache = true;
        }
      }
    }
  } else {
    // POST: (url, data, config) 或 (url, data, 'nocache', config)
    if (args.length >= 3) {
      if (typeof args[2] === "string" && args[2] === "nocache") {
        nocache = true;
        if (args[3] && typeof args[3] === "object") {
          config = args[3];
        }
      } else if (typeof args[2] === "object") {
        config = args[2];
        if (config.cache === "nocache") {
          nocache = true;
        }
      }
    }
  }

  // 检查其他nocache标识
  if (args.includes("nocache")) {
    nocache = true;
  }

  // 如果有自定义headers，将其纳入缓存键
  if (config.headers && typeof config.headers === "object") {
    const headersStr = JSON.stringify(config.headers);
    cacheKey = `${url}_headers_${headersStr}`;
  }

  if (
    url &&
    !nocache &&
    globalCacheData[cacheKey] &&
    globalCacheData[cacheKey]["time"] > new Date().getTime()
  ) {
    return globalCacheData[cacheKey]["data"];
  }
  return false;
};

const request = {
  get() {
    const args = Array.from(arguments);
    const cache = cacheData(args, "get");
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }

    // 处理参数传递给axios.get
    const url = args[0];
    let config = {};

    if (args.length === 2) {
      if (typeof args[1] === "string" && args[1] === "nocache") {
        // (url, 'nocache') - 不传config
        return ajax.get(url);
      } else {
        // (url, config)
        return ajax.get(url, args[1]);
      }
    } else if (args.length === 3) {
      // (url, 'nocache', config) - 合并config
      config = args[2] || {};
      return ajax.get(url, config);
    } else {
      // (url)
      return ajax.get(url);
    }
  },

  post() {
    const args = Array.from(arguments);
    const cache = cacheData(args, "post");
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }

    // 处理参数传递给axios.post
    const url = args[0];

    // 针对 request.post(url, "nocache") 的特殊情况，没有传 data
    if (args.length === 2 && args[1] === "nocache") {
      return ajax.post(url);
    } 
    else if (args.length === 2) {
      // (url, data)
      return ajax.post(url, args[1]);
    } 
    else if (args.length === 3) {
      if (args[2] === "nocache") {
        // (url, data, 'nocache')
        return ajax.post(url, args[1]);
      } else {
        // (url, data, config)
        return ajax.post(url, args[1], args[2]);
      }
    } 
    else if (args.length === 4 && args[2] === "nocache") {
      // (url, data, 'nocache', config)
      return ajax.post(url, args[1], args[3]);
    } 
    else {
      // 其他情况兜底
      return ajax.post(url, args[1]);
    }
  },

  delete() {
    const args = Array.from(arguments);
    const cache = cacheData(args, "delete");
    if (cache) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(cache);
        }, 100);
      });
    }

    // 处理参数传递给axios.delete
    const url = args[0];
    let config = {};

    if (args.length === 2) {
      if (typeof args[1] === "string" && args[1] === "nocache") {
        // (url, 'nocache')
        return ajax.delete(url);
      } else {
        // (url, config)
        return ajax.delete(url, args[1]);
      }
    } else if (args.length === 3) {
      // (url, 'nocache', config)
      config = args[2] || {};
      return ajax.delete(url, config);
    } else {
      // (url)
      return ajax.delete(url);
    }
  },
};

export default request;